import React, { useState } from "react";
import { FiArrowDown, FiArrowUp, FiArrowUpRight, FiBarChart2, FiChevronLeft, FiChevronRight, FiInfo, FiSearch } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import CoinIcon from "../components/CoinIcon";
import { useBinanceTicker, useBinanceTickers } from "../services/BinanceTickerContext";
import { marketAsset, marketChange, marketName, marketNetworks, marketPrice, marketSymbol, useMarketAssets } from "../services/useMarketAssets";
import "./Markets.css";

function AssetMark({ market }) {
    const asset = marketAsset(market);
    return <CoinIcon className="token-icon" symbol={marketSymbol(market)} logo={asset.logo} name={marketName(market)} />;
}

function MarketSummaryRow({ market }) {
    const symbol = marketSymbol(market);
    const ticker = useBinanceTicker(symbol);
    const change = marketChange(ticker);
    const tradePath = `/trade?symbol=${encodeURIComponent(market.market_symbol ?? market.symbol ?? `${symbol}USDT`)}`;
    return <Link className="summary-row" to={tradePath} key={symbol} aria-label={`Trade ${symbol}`}>
        <span className="summary-coin"><AssetMark market={market} />{symbol}</span>
        <span>{marketPrice(ticker)}</span>
        <em className={change.startsWith("+") ? "up" : change === "—" ? "" : "down"}>{change}</em>
    </Link>;
}

function SummaryCard({ title, markets }) {
    return <article className="summary-card">
        <div className="summary-head"><b>{title}</b><Link to="/markets/tradingData">More <FiArrowUpRight aria-hidden="true" /></Link></div>
        {markets.length ? markets.slice(0, 3).map((market) => <MarketSummaryRow key={marketSymbol(market)} market={market} />) : <p className="market-empty-inline">No market data</p>}
    </article>;
}

function TokenRow({ market }) {
    const navigate = useNavigate();
    const symbol = marketSymbol(market);
    const ticker = useBinanceTicker(symbol);
    const change = marketChange(ticker);
    const networks = marketNetworks(market);
    const detailPath = `/markets/asset/${encodeURIComponent(symbol)}`;
    const tradePath = `/trade?symbol=${encodeURIComponent(market.market_symbol ?? market.symbol ?? `${symbol}USDT`)}`;
    return <div className="token-row" role="row" tabIndex={0} aria-label={`Open ${symbol} trading page`} onClick={() => navigate(tradePath)} onKeyDown={(event) => {
        if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        navigate(tradePath);
    }}>
        <div className="token-label" role="cell"><AssetMark market={market} /><span><b>{symbol}</b><small>{marketName(market)}</small></span></div>
        <span role="cell">{marketPrice(ticker)}</span>
        <em className={change.startsWith("+") ? "up" : change === "—" ? "" : "down"} role="cell">{change}</em>
        <span role="cell">{ticker?.volume == null ? "—" : ticker.volume.toLocaleString("en-US")}</span>
        <span role="cell">{networks.map((network) => network.name ?? network.symbol).filter(Boolean).join(", ") || "—"}</span>
        <div className="token-actions" role="cell">
            <Link to={tradePath} onClick={(event) => event.stopPropagation()} aria-label={`Trade ${symbol}`} title={`Trade ${symbol}`}><FiBarChart2 /></Link>
            <Link to={detailPath} onClick={(event) => event.stopPropagation()} aria-label={`View ${symbol} details`} title={`View ${symbol} details`}><FiInfo /></Link>
        </div>
    </div>;
}

function MarketLoadingRows() {
    return <>
        <p className="market-loading-status" role="status">Loading markets</p>
        {Array.from({ length: 8 }, (_, index) => <div className="token-row market-skeleton-row" role="row" aria-hidden="true" key={index}>
            <span className="market-skeleton-asset" role="cell"><i className="market-skeleton market-skeleton-icon" /><span><i className="market-skeleton market-skeleton-symbol" /><i className="market-skeleton market-skeleton-name" /></span></span>
            <span role="cell"><i className="market-skeleton market-skeleton-price" /></span>
            <span role="cell"><i className="market-skeleton market-skeleton-change" /></span>
            <span role="cell"><i className="market-skeleton market-skeleton-volume" /></span>
            <span role="cell"><i className="market-skeleton market-skeleton-network" /></span>
            <span role="cell"><i className="market-skeleton market-skeleton-actions" /></span>
        </div>)}
    </>;
}

function SortHeading({ column, label, sort, onSort }) {
    const active = sort.column === column;
    return <span role="columnheader" aria-sort={active ? (sort.direction === 1 ? "ascending" : "descending") : "none"}>
        <button type="button" className="market-sort-button" onClick={() => onSort(column)}>
            {label}{active && (sort.direction === 1 ? <FiArrowUp aria-hidden="true" /> : <FiArrowDown aria-hidden="true" />)}
        </button>
    </span>;
}

function Overview() {
    const pageSize = 20;
    const { assets, loading, error } = useMarketAssets();
    const tickers = useBinanceTickers();
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("all");
    const [network, setNetwork] = useState("all");
    const [sort, setSort] = useState({ column: "symbol", direction: 1 });
    const [page, setPage] = useState(1);
    const categories = [...new Set(assets.map((market) => market.category).filter(Boolean))];
    const networks = [...new Set(assets.flatMap((market) => marketNetworks(market).map((item) => item.name ?? item.symbol)).filter(Boolean))];
    const filteredAssets = assets.filter((market) => {
        const marketNetworksText = marketNetworks(market).map((item) => `${item.name ?? ""} ${item.symbol ?? ""}`).join(" ");
        const matchesSearch = `${marketSymbol(market)} ${marketName(market)} ${marketNetworksText}`.toLowerCase().includes(search.trim().toLowerCase());
        return matchesSearch && (category === "all" || market.category === category) && (network === "all" || marketNetworks(market).some((item) => (item.name ?? item.symbol) === network));
    });
    const sortedAssets = [...filteredAssets].sort((left, right) => {
        const value = (market) => {
            const ticker = tickers[marketSymbol(market)];
            if (sort.column === "price") return ticker?.price;
            if (sort.column === "change") return ticker?.change;
            if (sort.column === "volume") return ticker?.volume;
            return marketSymbol(market).toLowerCase();
        };
        const leftValue = value(left);
        const rightValue = value(right);
        if (leftValue == null) return rightValue == null ? 0 : 1;
        if (rightValue == null) return -1;
        const compared = typeof leftValue === "string" ? leftValue.localeCompare(rightValue) : leftValue - rightValue;
        return compared * sort.direction;
    });
    const pageCount = Math.max(1, Math.ceil(sortedAssets.length / pageSize));
    const currentPage = Math.min(page, pageCount);
    const paginatedAssets = sortedAssets.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const sortBy = (column) => {
        setSort((current) => ({
            column,
            direction: current.column === column ? current.direction * -1 : (column === "symbol" ? 1 : -1),
        }));
        setPage(1);
    };
    const volumeRanked = [...assets].sort((left, right) => (tickers[marketSymbol(right)]?.volume ?? 0) - (tickers[marketSymbol(left)]?.volume ?? 0));
    const gainers = [...assets].sort((left, right) => (tickers[marketSymbol(right)]?.change ?? -Infinity) - (tickers[marketSymbol(left)]?.change ?? -Infinity));
    const newest = [...assets].sort((left, right) => new Date(right.created_at ?? 0) - new Date(left.created_at ?? 0));
    const summaries = [
        ["Hot", volumeRanked],
        ["New", newest],
        ["Top Gainer", gainers],
        ["Top Volume", volumeRanked],
    ];

    return (
        <div className="market-overview">
            {assets.length > 1 && <div className="summary-grid">
                {summaries.map(([title, markets]) => <SummaryCard key={title} title={title} markets={markets} />)}
            </div>}
            <div className="token-heading">
                <h2>Markets</h2>
                <p>Live prices, 24-hour changes, volume, and supported networks.</p>
                <Link to="/markets/tradingData">Trading Data <FiArrowUpRight aria-hidden="true" /></Link>
            </div>
            {error && <p className="market-state market-state-error" role="alert">{error}</p>}
            <div className="market-overview-controls">
                <label className="market-search-control">
                    <FiSearch aria-hidden="true" />
                    <input type="search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search assets or networks" aria-label="Search assets or networks" />
                </label>
                {categories.length > 0 && <select value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }} aria-label="Filter by category"><option value="all">All categories</option>{categories.map((value) => <option key={value} value={value}>{value}</option>)}</select>}
                {networks.length > 0 && <select value={network} onChange={(event) => { setNetwork(event.target.value); setPage(1); }} aria-label="Filter by network"><option value="all">All networks</option>{networks.map((value) => <option key={value} value={value}>{value}</option>)}</select>}
                <span className="market-result-count">{filteredAssets.length} of {assets.length} markets</span>
            </div>
            <div className="token-table" role="table" aria-label="Market assets" aria-busy={loading}>
                <div className="token-table-head" role="row">
                    <SortHeading column="symbol" label="Asset" sort={sort} onSort={sortBy} />
                    <SortHeading column="price" label="Price" sort={sort} onSort={sortBy} />
                    <SortHeading column="change" label="24h Change" sort={sort} onSort={sortBy} />
                    <SortHeading column="volume" label="24h Volume" sort={sort} onSort={sortBy} />
                    <span role="columnheader">Networks</span><span role="columnheader">Actions</span>
                </div>
                {loading ? <MarketLoadingRows /> : paginatedAssets.map((market) => <TokenRow key={marketSymbol(market)} market={market} />)}
                {!loading && !error && assets.length === 0 && <p className="market-state">No active markets are configured.</p>}
            </div>
            {!loading && !error && sortedAssets.length > 0 && <nav className="market-pagination" aria-label="Market pages">
                <span>Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, sortedAssets.length)} of {sortedAssets.length}</span>
                <div>
                    <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={currentPage === 1} aria-label="Previous page"><FiChevronLeft aria-hidden="true" /></button>
                    <span aria-live="polite">Page {currentPage} of {pageCount}</span>
                    <button type="button" onClick={() => setPage((value) => Math.min(pageCount, value + 1))} disabled={currentPage === pageCount} aria-label="Next page"><FiChevronRight aria-hidden="true" /></button>
                </div>
            </nav>}
        </div>
    );
}

export default Overview;
